import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5041';

const PUBLIC_ENDPOINTS = [
  '/api/auth/login',
  '/api/auth/register',
  '/api/auth/google',
  '/api/auth/google/callback',
  '/api/auth/verify-email',
  '/api/auth/resend-verification',
  '/api/auth/refresh',
];

const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Token refresh state
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });
  failedQueue = [];
};

const isPublicEndpoint = (url) => {
  return PUBLIC_ENDPOINTS.some(endpoint => url.startsWith(endpoint));
};

// REQUEST INTERCEPTOR: Add auth token
axiosInstance.interceptors.request.use(
  (config) => {
    if (isPublicEndpoint(config.url)) {
      return config;
    }

    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// RESPONSE INTERCEPTOR: Handle 401 and token refresh
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Not 401 or already refresh endpoint → reject immediately
    if (error.response?.status !== 401 || originalRequest.url === '/api/auth/refresh') {
      return Promise.reject(error);
    }

    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) {
      localStorage.removeItem('authToken');
      window.location.href = '/login';
      return Promise.reject(error);
    }

    // Queue requests if refresh already in progress
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      })
        .then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return axiosInstance(originalRequest);
        })
        .catch((err) => Promise.reject(err));
    }

    // Start refresh process
    originalRequest._retry = true;
    isRefreshing = true;

    try {
      // Call refresh directly (avoid interceptor loop)
      const response = await axios.post(
        `${API_BASE_URL}/api/auth/refresh`,
        { refreshToken },
        { headers: { 'Content-Type': 'application/json' } }
      );

      const newAccessToken = response.data.accessToken; // Backend returns 'accessToken'
      const newRefreshToken = response.data.refreshToken;

      if (!newAccessToken || !newRefreshToken) {
        throw new Error('Invalid refresh response: missing tokens');
      }

      localStorage.setItem('authToken', newAccessToken);
      localStorage.setItem('refreshToken', newRefreshToken);

      originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
      processQueue(null, newAccessToken);

      return axiosInstance(originalRequest);
    } catch (refreshError) {

      // Check for token reuse detection
      if (refreshError.response?.data?.errorMessage?.includes('Token reuse detected')) {
        alert('Security alert: Session revoked due to suspicious activity. Please log in again.');
      }

      processQueue(refreshError, null);

      localStorage.removeItem('authToken');
      localStorage.removeItem('refreshToken');
      window.location.href = '/login';

      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);

export default axiosInstance;
